/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-mixins, no-unused-vars, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import config from 'linced-app/config/environment';
import { action, computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { service } from '@ember/service';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import StudentReportState from '@linc-technologies/linced-common/enums/reports/student-report-state';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { isiOS } from '@linc-technologies/linced-common/utils/platform';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';
import { templateDisplayName } from 'linced-app/utils/reporting';
import { serializeQueryParams } from 'linced-app/utils/url';

// Normalises a period index for use as a query param. Index 0 is a real
// period, so it cannot be collapsed with a falsy check. Anything that isn't a
// number becomes null, which keeps the param out of the URL and lets the
// destination route fall back to the current period.
function qpPeriod(period) {
  return period && typeof period.index === 'number' ? period.index : null;
}

// The period's end date addresses it across template versions, which its index
// cannot: a version that inserts or removes a period renumbers the rest, so
// one period's reports can sit at different indices on different versions.
// Null keeps the param out of the URL.
function qpPeriodEnd(period) {
  return period && period.endDate ? period.endDate : null;
}

// The academic year the period belongs to. Historic cards can sit in a past
// year, and the write and review screens filter their report query by year —
// so without this they ask for the right period in the wrong year and come
// back empty. Null keeps the param out of the URL and the destination falls
// back to the current year, which is correct for every active card.
function qpPeriodYear(period) {
  return period && typeof period.year === 'number' ? period.year : null;
}

export default class GroupController extends Controller.extend(
  ReportingBaseMixin,
) {
  // publishNavBlocked records that the route has already toasted a blocked
  // navigation during the current publish run.
  publishNavBlocked = false;

  @service
  dates;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  studentReportActions;

  @service
  toaster;

  isiOS = isiOS();
  selectedGroup = null;
  @tracked templateDraftInfo = null;
  @tracked
  isLoadingDraftInfo = false;
  @tracked historicYears = null;
  @tracked
  isLoadingHistoric = false;
  @tracked
  isCreatingDrafts = false;
  @tracked
  loadingReportId = null;
  @tracked
  activeReport = null;
  isWriteRoute = false;
  isReviewRoute = false;

  // Sidepeek state
  @tracked
  sidepeekOpen = false;

  @tracked selectedReport = null;
  // The period index of the card that opened the sidepeek — what new drafts
  // are stamped with. Null only when the sidepeek was opened from somewhere
  // that has no card behind it.
  @tracked selectedPeriodIndex = null;
  @tracked
  selectedStudents = null;

  // Publish state
  @tracked
  publishSidepeekOpen = false;

  // The period the open publish sidepeek belongs to, so a retry after a
  // partial run reloads that period rather than the current one.
  @tracked publishPeriod = null;
  @tracked
  publishStudents = null;
  @tracked publishStudentReports = null;
  @tracked
  selectedPublishStudents = null;
  @tracked
  isPublishing = false;
  @tracked
  isChangingStatus = false;
  @tracked
  isOpeningPublishReports = false;

  init() {
    super.init(...arguments);
    this.set('templateDraftInfo', {});
    this.historicYears = null;
    this.isCreatingDrafts = false;
    this.loadingReportId = null;
    this.publishSidepeekOpen = false;
    this.publishStudents = null;
    this.publishStudentReports = null;
    this.selectedPublishStudents = [];
    this.isPublishing = false;
    this.isChangingStatus = false;
    this.isOpeningPublishReports = false;
  }

  @filterBy('visibleGroups', 'type', GroupType.HOMEROOM)
  classes;

  @filterBy('visibleGroups', 'type', GroupType.GROUP)
  groups;

  @filterBy('visibleGroups', 'type', GroupType.SHARED)
  shared;

  @filterBy('visibleGroups', 'type', GroupType.AUTO)
  autogroups;

  @computed(
    'parentController.{selectedGroupType,groupSearch}',
    'classes',
    'groups',
    'shared',
    'autogroups',
  )
  get filteredGroups() {
    let search = (this.parentController.get('groupSearch') || '')
      .toLowerCase()
      .trim();
    if (search) {
      let allGroups = [
        ...this.classes,
        ...this.groups,
        ...this.shared,
        ...this.autogroups,
      ];
      return allGroups
        .filter((group) => {
          let label = (group.get('label') || '').toLowerCase();
          let name = (group.get('name') || '').toLowerCase();
          return label.includes(search) || name.includes(search);
        })
        .sort(naturalSort('name'));
    }
    let type = this.parentController.get('selectedGroupType');
    return (this[type] || []).slice().sort(naturalSort('name'));
  }

  @computed('selectedGroup.sortedCurrentLearners.@each.person')
  get sidepeekStudents() {
    let learners = this.get('selectedGroup.sortedCurrentLearners');
    if (!learners) {
      return [];
    }
    return learners.filter((l) => l.person).map((l) => l.person);
  }

  @computed('selectedStudents.[]')
  get selectedCount() {
    return this.selectedStudents ? this.selectedStudents.length : 0;
  }

  // True when any selected publish-sidepeek student has a Ready-to-Publish
  // report written against a superseded template version (i.e. its
  // reportTemplateId differs from the active/current template). Reverting
  // those reports to Ready-to-Review would put the teacher back into a
  // write surface bound to a stale template, so the option is disabled
  // until the user deselects them.
  @computed(
    'selectedPublishStudents.[]',
    'publishStudentReports',
    'activeReport.id',
  )
  get selectedHaveStaleTemplate() {
    let activeId = this.get('activeReport.id');
    if (!activeId) return false;
    let reportMap = this.publishStudentReports || {};
    return (this.selectedPublishStudents || []).some((student) => {
      let report = reportMap[student.get('id')];
      if (!report) return false;
      return report.belongsTo('reportTemplateId').id() !== activeId;
    });
  }

  // Students (across all publishStudents, not just selected ones) whose reports
  // were written against a superseded template version.
  @computed('publishStudents.[]', 'publishStudentReports', 'activeReport.id')
  get stalePublishStudents() {
    let activeId = this.get('activeReport.id');
    if (!activeId) return [];
    let reportMap = this.publishStudentReports || {};
    return (this.publishStudents || []).filter((student) => {
      let report = reportMap[student.get('id')];
      return report && report.belongsTo('reportTemplateId').id() !== activeId;
    });
  }

  // The currently-selected students whose report is on an older template
  // version — listed in the template-sync confirmation dialog.
  @computed(
    'selectedPublishStudents.[]',
    'publishStudentReports',
    'activeReport.id',
  )
  get selectedStaleStudents() {
    let activeId = this.get('activeReport.id');
    if (!activeId) return [];
    let reportMap = this.publishStudentReports || {};
    return (this.selectedPublishStudents || []).filter((student) => {
      let report = reportMap[student.get('id')];
      return report && report.belongsTo('reportTemplateId').id() !== activeId;
    });
  }

  // True while the open publish sidepeek belongs to a closed reporting period.
  // Set from the card that opened it — a historic card's entry lives in
  // historicYearsData, not templateDraftInfo, so there is nothing to look it up
  // from here.
  //
  // Migrating a report onto a newer template version is unavailable for those
  // periods. syncTemplate rewrites a report against the current template, and
  // a template edit can insert, remove or reorder its periods, so for a period
  // that closed before those edits we cannot show that the report would still
  // belong to the period it was written for. Periods are now locked once a
  // report exists against them, which holds going forward, but templates
  // reshuffled before that landed are already in the data.
  get isHistoricPublishPeriod() {
    return !!this.publishPeriod?.isHistoric;
  }

  // Confirmation dialog shown when moving back a selection that includes
  // reports on an older template version.
  @tracked
  showTemplateSyncDialog = false;

  // Selected reports that can be moved back without a template sync. Drives the
  // dialog's remaining action on a historic period, where syncing is off the
  // table and the whole selection can turn out to be unmovable.
  @computed('selectedPublishStudents.[]', 'selectedStaleStudents.[]')
  get selectedCurrentVersionCount() {
    return (
      (this.selectedPublishStudents || []).length -
      (this.selectedStaleStudents || []).length
    );
  }

  // The student-report records for the currently selected students, fed to the
  // PublishReportsRunner so it can capture visuals + publish each in sequence.
  @computed('selectedPublishStudents.[]', 'publishStudentReports')
  get selectedPublishReports() {
    let reportMap = this.publishStudentReports || {};
    return (this.selectedPublishStudents || [])
      .map((student) => reportMap[student.get('id')])
      .filter(Boolean);
  }

  // Registered by PublishReportsRunner; calling it kicks off the
  // capture-then-publish loop and resolves when the whole batch is done.
  @tracked
  _runPublishCapture = null;

  // rosterUnavailable is true when the counts response carried no assigned-
  // student denominator even though the group visibly has students. The
  // backend populates totalAssignedStudents and missingStudentIds together,
  // only once it has resolved a roster, so a zero denominator against a
  // non-empty group means "we could not determine who is assigned".
  //
  // This matters because studentsWithReports infers "already has a report"
  // from *absence* in missingStudentIds. Without this guard an unresolved
  // roster produces an empty missing list, every student is silently treated
  // as done, and the drawer claims all reports are created.
  @computed('selectedReport.id', 'sidepeekStudents.[]', 'templateDraftInfo')
  get rosterUnavailable() {
    let draftInfo =
      this.selectedReport && this.templateDraftInfo[this.selectedReport.id];
    if (!draftInfo) {
      return false;
    }
    return (
      !draftInfo.totalAssignedStudents &&
      (this.sidepeekStudents || []).length > 0
    );
  }

  @computed(
    'rosterUnavailable',
    'selectedReport.id',
    'sidepeekStudents.[]',
    'templateDraftInfo',
  )
  get studentsWithReports() {
    let draftInfo =
      this.selectedReport && this.templateDraftInfo[this.selectedReport.id];
    if (!draftInfo || this.rosterUnavailable) {
      return [];
    }
    let missingIds = new Set(draftInfo.missingStudentIds || []);
    return this.sidepeekStudents.filter((s) => !missingIds.has(s.get('id')));
  }

  @computed('sidepeekStudents.[]', 'studentsWithReports.[]')
  get selectableStudents() {
    let existingIds = new Set(
      (this.studentsWithReports || []).map((s) => s.get('id')),
    );
    return this.sidepeekStudents.filter((s) => !existingIds.has(s.get('id')));
  }

  @computed('selectedStudents.[]', 'selectableStudents.[]')
  get allSelected() {
    if (!this.selectedStudents || !this.selectableStudents.length) {
      return false;
    }
    return this.selectedStudents.length === this.selectableStudents.length;
  }

  // activeCards is the Active timeline, built from the counts response rather
  // than from the template list.
  //
  // The backend sends one entry per active template — counted or not, since it
  // synthesises empty entries — each naming the period it resolved against the
  // school's rollover. So the response is the complete answer to "what is
  // active, and for which period", and nothing here recomputes it. Only
  // templates open for writing (active and not deleted) are sent; a retired
  // template's current period comes back in the historic response instead.
  @computed(
    'templateDraftInfo',
    'model.reportTemplates.[]',
    'dates.currentAcademicYear',
  )
  get activeCards() {
    let drafts = this.templateDraftInfo || {};
    let year = this.dates.currentAcademicYear;
    let cards = [];

    for (let [templateId, entry] of Object.entries(drafts)) {
      if (!entry) continue;
      let template = this.store.peekRecord(
        'reports/report-template',
        templateId,
      );
      // An entry whose template record never loaded has nothing to render
      // from — no name, no sections. Every entry is an active template, so
      // the reporting route's query has already loaded it.
      if (!template) continue;

      // Named for the period the entry describes, so a card and the screen
      // it opens agree. The routes rebuild this too, since the record is
      // shared and whichever screen ran last would otherwise win.
      template.set(
        'displayName',
        templateDisplayName(template, entry.periodIndex, year, entry),
      );

      cards.push({
        template,
        drafts: entry,
        periodIndex:
          typeof entry.periodIndex === 'number' ? entry.periodIndex : 0,
        isTemplateDeleted: entry.isTemplateDeleted === true,
      });
    }

    // Newest period first, on the backend's resolved end date — the only
    // ordering that stays right for a template whose periods cross the
    // calendar year, where the month alone says the opposite.
    return cards.sort((a, b) => {
      let aEnd = a.drafts.periodEndDate || '';
      let bEnd = b.drafts.periodEndDate || '';
      if (aEnd !== bEnd) return bEnd.localeCompare(aEnd);
      return (a.template.get('name') || '').localeCompare(
        b.template.get('name') || '',
      );
    });
  }

  // historicYearsData pairs each backend entry with its template object and
  // a draft summary, ready for the Historic section to render with the same
  // Reporting::ReportTimelineCard component the Active section uses. Returns
  // null when no historic data is loaded yet; an empty array means loaded
  // but no past-period entries exist.
  @computed('historicYears', 'model.reportTemplates.[]')
  get historicYearsData() {
    let years = this.historicYears;
    if (!years) return null;
    if (!years.length) return [];

    let templatesById = {};
    for (let t of this.get('model.reportTemplates') || []) {
      templatesById[t.get('id')] = t;
    }
    let resolveTemplate = (id) =>
      templatesById[id] ||
      this.store.peekRecord('reports/report-template', id) ||
      null;

    return years
      .map((yg) => ({
        year: yg.year,
        periods: (yg.periods || [])
          .map((pg) => ({
            periodIndex: pg.periodIndex,
            periodLabel: pg.periodLabel,
            items: (pg.entries || [])
              .map((entry) => {
                let template = resolveTemplate(entry.templateId);
                if (!template) return null;
                return {
                  template,
                  periodIndex: pg.periodIndex,
                  drafts: this._buildEntryFromCounts(entry, {
                    historic: true,
                    // The response nests entries under their year, so the
                    // group's year is always present and authoritative. The
                    // entry usually repeats it; if it ever doesn't, the card
                    // would fall back to the current year and send the write
                    // and review screens looking in the wrong one.
                    fallbackYear: yg.year,
                  }),
                };
              })
              .filter(Boolean),
          }))
          .filter((p) => p.items.length),
      }))
      .filter((y) => y.periods.length);
  }

  // historic changes what "completed" means. For the current period every
  // assigned student should end up with a published report, so a student
  // without one is unfinished work. A closed period cannot be finished that
  // way any more — new reports can't be started for it — so measuring it
  // against the roster would leave cards permanently stuck at "in progress"
  // for students nobody ever intended to report on. Historic completion is
  // therefore about the reports that exist: everything started for that
  // period has been published.
  _buildEntryFromCounts(entry, { historic = false, fallbackYear = null } = {}) {
    let inProgressIds = entry.inProgress || [];
    let readyToReviewIds = entry.readyToReview || [];
    let proofreadingIds = entry.proofreading || [];
    let readyToPublishIds = entry.readyToPublish || [];
    let publishedIds = entry.published || [];
    let readyForLeadershipCommentIds = entry.readyForLeadershipComment || [];

    let missingStudentIds = entry.missingStudentIds || [];
    let missingCount = missingStudentIds.length;
    let missingStudentsPreview = missingStudentIds
      .slice(0, 5)
      .map((id) => this.store.peekRecord('person', id))
      .filter(Boolean);

    let nothingQueued =
      inProgressIds.length === 0 &&
      readyToReviewIds.length === 0 &&
      proofreadingIds.length === 0 &&
      readyToPublishIds.length === 0 &&
      readyForLeadershipCommentIds.length === 0;

    return {
      leadershipCommentLabel: entry.leadershipCommentLabel || null,
      inProgress: inProgressIds.length,
      readyToReview: readyToReviewIds.length,
      proofreading: proofreadingIds.length,
      readyToPublish: readyToPublishIds.length,
      published: publishedIds.length,
      readyForLeadershipComment: readyForLeadershipCommentIds.length,
      inProgressIds,
      readyToReviewIds,
      proofreadingIds,
      readyToPublishIds,
      publishedIds,
      readyForLeadershipCommentIds,
      missingStudentIds,
      missingCount,
      // Whether anything has been started for this period. The backend now
      // sends an entry for every active template, counted or not, so the
      // presence of an entry no longer answers this — the counts do.
      hasAnyReports: publishedIds.length > 0 || !nothingQueued,
      // The academic year this entry belongs to. A historic card needs it to
      // re-query its own reports; without it the publish flow would look in
      // the current year and find nothing.
      year:
        typeof entry.year === 'number'
          ? entry.year
          : typeof fallbackYear === 'number'
            ? fallbackYear
            : null,
      periodStartDate: entry.periodStartDate || '',
      periodEndDate: entry.periodEndDate || '',
      // The period this entry's counts describe, resolved by the backend
      // against the school's rollover. The card must act on THIS index — it is
      // what new drafts are stamped with and what the write, review and view
      // routes filter on — so nothing here recomputes "which period is it".
      periodIndex:
        typeof entry.periodIndex === 'number' ? entry.periodIndex : null,
      // The assigned-student denominator. The backend only populates this
      // (and missingStudentIds) once it has resolved a roster, so a zero here
      // means "roster unresolved", not "nobody assigned" — see
      // rosterUnavailable.
      totalAssignedStudents:
        typeof entry.totalAssignedStudents === 'number'
          ? entry.totalAssignedStudents
          : 0,
      // Historic: students with no report for this period are deliberately
      // not counted against it — see the note above.
      //
      // Current period: deliberately NOT taken from the response's
      // isFullyCompleted. It is a proto3 bool serialised with omitempty, so a
      // false arrives as no key at all and cannot be told apart from an older
      // response that never carried the field — the flag can confirm
      // completion but can never deny it. Server-side it is also
      // `publishedStudents == totalAssignedStudents`, which ignores reports
      // still in a queue and is vacuously true for an empty group. The local
      // check derives from missingStudentIds — the residue of the same roster
      // resolution — and agrees with the flag wherever the flag can speak.
      isCompleted: historic
        ? publishedIds.length > 0 && nothingQueued
        : publishedIds.length > 0 && missingCount === 0 && nothingQueued,
      missingStudentsPreview,
      isTemplateDeleted: entry.isTemplateDeleted === true,
      templateName: entry.templateName || '',
      periodLabel: entry.periodLabel || '',
    };
  }

  // Sequencing state for the two count loaders below (historic also
  // coalesces). Both are driven from reporting.group.index's routeDidChange
  // hook, which fires on every arrival at the hub — entering, switching group,
  // and returning from write / review / view / a student. Each arrival
  // fetches; the sequence number stops a slow earlier response from
  // overwriting a newer one.
  //
  // Active reloads on every arrival. Historic does not — see
  // refreshHistoricIfNeeded.
  _draftInfoSeq = 0;
  _historicRequest = null;
  _historicKey = null;
  _historicSeq = 0;

  // The _countParams key the currently-held active counts were fetched under.
  // Tells a group switch (the held data belongs to a different group and must
  // be dropped) apart from a refresh of the same group (keep it so the section
  // does not flicker away and back).
  _draftInfoLoadedKey = null;

  // The _countParams key the currently-held historic data was fetched under.
  // Historic counts are not re-queried on every return to the hub, so this is
  // what tells a group switch (must refetch) apart from a return trip (need
  // not). Stays null after a failed fetch, so the next entry retries.
  _historicLoadedKey = null;

  // Set when something that can change a past period has happened since the
  // last historic fetch — an action launched from a historic card, or a void /
  // reopen / recreate on the student page. Cleared by the refresh it triggers.
  _historicStale = false;

  // Called by anything outside this controller that mutates a report which may
  // belong to a closed period.
  markHistoricStale() {
    this._historicStale = true;
  }

  // The hub's historic refresh policy, driven by reporting.group.index's
  // routeDidChange hook. Active counts refresh on every return because the
  // current period is what the user was just working in; past periods are not.
  // Returning from a screen an active card opened cannot have changed one, so
  // refetching then is a request per navigation for a section that did not
  // move. Historic reloads only when this group's data isn't loaded yet
  // (entering, or switching group) or something marked it stale.
  refreshHistoricIfNeeded() {
    let group = this.selectedGroup;
    if (!group) return Promise.resolve();

    let query = serializeQueryParams(this._countParams(group));
    if (this._historicLoadedKey === query && !this._historicStale) {
      return Promise.resolve();
    }
    this._historicStale = false;
    return this.loadGroupHistoric();
  }

  // Shared request shape for the count loaders. currentAcademicYear and
  // currentMonth are what let the backend resolve each template's current
  // period against this caller's clock rather than the server's: the month
  // carries our timezone, and the academic year is the school's, which need
  // not be the calendar year.
  _countParams(group, extra) {
    return Object.assign(
      {
        schoolId: this.session.activeSchool,
        groupId: group ? group.get('id') : null,
        currentAcademicYear: this.dates.currentAcademicYear,
        currentMonth: new Date().getMonth() + 1,
      },
      extra,
    );
  }

  loadGroupReports() {
    let group = this.selectedGroup;
    if (!group) return Promise.resolve();

    let query = serializeQueryParams(this._countParams(group));
    // Always a fresh request. Handing back an in-flight one returns counts
    // fetched before whatever the caller just changed (drafts created on the
    // student page, a move back), so the hub and the write route disagree
    // until the next visit. The sequence check in _fetchGroupReports keeps the
    // newest response.
    let seq = ++this._draftInfoSeq;
    return this._fetchGroupReports(query, seq);
  }

  async _fetchGroupReports(query, seq) {
    // Data we hold belongs to a different group: drop it now. Leaving it in
    // place renders the previous group's cards under the new group's header,
    // and they are clickable — write/review/view/publish would open with this
    // group and that group's template and period. It also never self-corrects
    // if the request fails.
    if (this._draftInfoLoadedKey !== query) {
      this.set('templateDraftInfo', {});
      this._draftInfoLoadedKey = null;
      this.notifyPropertyChange('templateDraftInfo');
    }
    this.isLoadingDraftInfo = true;

    try {
      let data = await this.parentController.fetch.request(
        `${config.APP.apiHost}/reports/v4/group-student-report-counts?${query}`,
      );

      // Superseded by a newer request — drop this response rather than let it
      // overwrite fresher data.
      if (seq !== this._draftInfoSeq) return;

      let builtDrafts = {};
      for (let entry of data.entries || []) {
        builtDrafts[entry.templateId] = this._buildEntryFromCounts(entry);
      }
      this.set('templateDraftInfo', builtDrafts);
      this.notifyPropertyChange('templateDraftInfo');
      this._draftInfoLoadedKey = query;
    } catch (err) {
      if (seq !== this._draftInfoSeq) return;
      this.toaster.error(
        'Failed to load report drafts. Please refresh the page and try again.',
      );
    } finally {
      if (seq === this._draftInfoSeq) {
        this.isLoadingDraftInfo = false;
      }
    }
  }

  loadGroupHistoric() {
    let group = this.selectedGroup;
    if (!group) return Promise.resolve();

    let query = serializeQueryParams(this._countParams(group));
    if (this._historicRequest && this._historicKey === query) {
      return this._historicRequest;
    }

    let seq = ++this._historicSeq;
    let request = this._fetchGroupHistoric(query, seq).finally(() => {
      if (this._historicSeq === seq) {
        this._historicRequest = null;
        this._historicKey = null;
      }
    });
    this._historicRequest = request;
    this._historicKey = query;
    return request;
  }

  async _fetchGroupHistoric(query, seq) {
    // Same reasoning as _fetchGroupReports: a different group's history must
    // not stay on screen, and stay actionable, while the new one loads.
    // historicYearsData reads null as "not loaded" and hides the section, so
    // clearing hides it rather than showing an empty-history message.
    if (this._historicLoadedKey !== query) {
      this.historicYears = null;
      this._historicLoadedKey = null;
    }
    this.isLoadingHistoric = true;

    try {
      let data = await this.parentController.fetch.request(
        `${config.APP.apiHost}/reports/v4/group-historic-report-counts?${query}`,
      );

      // Historic entries can reference retired templates that aren't loaded
      // by the reporting route (which queries isActive=true). Fetch any the
      // store doesn't know about before publishing the response, so
      // historicYearsData's resolveTemplate() sees them and doesn't silently
      // drop the entries.
      let referenced = new Set();
      for (let yg of data.years || []) {
        for (let pg of yg.periods || []) {
          for (let e of pg.entries || []) {
            if (e.templateId) referenced.add(e.templateId);
          }
        }
      }
      await this._loadMissingTemplates(referenced);

      // Superseded by a newer request — drop this response.
      if (seq !== this._historicSeq) return;

      this.historicYears = data.years || [];
      this._historicLoadedKey = query;
    } catch (err) {
      if (seq !== this._historicSeq) return;
      this.toaster.error(
        'Failed to load historic report data. Please refresh the page and try again.',
      );
    } finally {
      if (seq === this._historicSeq) {
        this.isLoadingHistoric = false;
      }
    }
  }

  // Fetches, in parallel, any of these template ids the store does not hold.
  // A template that fails to load resolves to null; the computeds skip it.
  _loadMissingTemplates(ids) {
    let missing = Array.from(ids).filter(
      (id) => !this.store.peekRecord('reports/report-template', id),
    );
    return Promise.all(
      missing.map((id) =>
        this.store.findRecord('reports/report-template', id).catch(() => null),
      ),
    );
  }

  async _refreshTemplateCounts(reportTemplateId) {
    let group = this.selectedGroup;
    if (!group) return;
    let groupKey = serializeQueryParams(this._countParams(group));
    try {
      let params = this._countParams(group, { reportTemplateId });
      let data = await this.parentController.fetch.request(
        `${
          config.APP.apiHost
        }/reports/v4/group-student-report-counts?${serializeQueryParams(
          params,
        )}`,
      );
      let entry = (data.entries || [])[0];
      if (!entry) return;
      // The user moved to another group while this was in flight; merging now
      // would drop one group's entry into another group's map.
      if (this._draftInfoLoadedKey !== groupKey) return;
      let drafts = Object.assign({}, this.templateDraftInfo);
      drafts[reportTemplateId] = this._buildEntryFromCounts(entry);
      this.set('templateDraftInfo', drafts);
      this.notifyPropertyChange('templateDraftInfo');
    } catch (err) {
      // non-critical — full reload happens when returning to the dashboard
    }
  }

  // Bypasses the closeSidepeek action's in-flight guard. Use this when the
  // controller itself wants to dismiss the sidepeek (e.g. after drafts have
  // finished) rather than honoring a user-initiated close attempt.
  _resetSidepeek() {
    this.sidepeekOpen = false;
    this.selectedReport = null;
    this.selectedPeriodIndex = null;
    this.selectedStudents = null;
    this.loadingReportId = null;
  }

  // Populate the publish sidepeek's student list from fresh ready-to-publish
  // reports. Shared by openPublishReports (initial open) and publishReports
  // (refresh after a run, so published students drop off and only the failed
  // ones — still ready to publish — remain for retry).
  async _loadPublishStudents(report, period) {
    let reportId = report.get('id');
    let group = this.selectedGroup;

    try {
      // The reports come from a period-scoped query, not from the Active
      // counts endpoint. That endpoint answers only for the period a template
      // is currently in, so opening publish on a historic card — a past period
      // still holding ready-to-publish work, which is exactly what those cards
      // are for — returned nothing at all.
      //
      // The card's own entry supplies the period and the academic year, so the
      // sidepeek lists the reports belonging to the card that opened it. Both
      // are passed in rather than looked up: a historic card's entry lives in
      // historicYearsData, not templateDraftInfo, so a lookup by template id
      // would miss precisely the cards that need this.
      let entry = (this.templateDraftInfo || {})[reportId] || {};
      let periodEndDate = period?.endDate || entry.periodEndDate;
      let periodIndex =
        typeof period?.index === 'number' ? period.index : entry.periodIndex;
      let academicYear =
        typeof period?.year === 'number'
          ? period.year
          : typeof entry.year === 'number'
            ? entry.year
            : this.dates.currentAcademicYear;

      let scope = {
        reportTemplateId: reportId,
        schoolId: this.session.activeSchool,
        groupId: group.get('id'),
        year: academicYear,
      };
      if (periodEndDate) {
        // Addresses the period across every version of the template, which an
        // index cannot: a template edited mid-period leaves one period's
        // reports at different indices on different versions. Implies
        // consolidation server-side.
        scope.periodEndDate = periodEndDate;
      } else if (typeof periodIndex === 'number') {
        // No resolved date (an orphaned index, whose period no longer exists).
        // Fall back to the index against this version alone — consolidating
        // would apply it across versions that may number periods differently.
        scope.periodIndex = periodIndex;
        scope.filterByPeriodIndex = true;
        scope.consolidateTemplateVersions = !report.get('next');
      }

      // Two queries rather than one: the API can filter TO reports awaiting a
      // leadership comment, but not away from them, so the awaiting set is
      // fetched and subtracted. Ready-to-publish reports still waiting on that
      // comment are not publishable.
      let [readyReports, awaitingReports] = await Promise.all([
        this.store.query('reports/student-report', {
          ...scope,
          state: StudentReportState.READY_TO_PUBLISH,
        }),
        this.store.query('reports/student-report', {
          ...scope,
          state: StudentReportState.READY_TO_PUBLISH,
          isAwaitingLeadershipComment: true,
        }),
      ]);

      let awaitingIds = new Set(awaitingReports.map((r) => r.get('id')));
      let publishReports = readyReports.filter(
        (r) => !awaitingIds.has(r.get('id')),
      );

      // Card counts refresh separately, and only when the endpoint actually
      // has something to say about this template — writing an empty entry back
      // would zero a historic card the moment its publish sidepeek opened.
      // Skipped entirely for a closed period: that endpoint answers for the
      // current period, so it would refresh a different card than the one the
      // user opened, for a sidepeek that has not changed anything yet.
      if (!period?.isHistoric) {
        this._refreshTemplateCounts(reportId);
      }

      let students = publishReports
        .map((r) =>
          this.store.peekRecord('person', r.belongsTo('studentId').id()),
        )
        .filter(Boolean);

      let reportMap = {};
      for (let r of publishReports) {
        reportMap[r.belongsTo('studentId').id()] = r;
      }

      this.publishSidepeekOpen = true;
      this.publishStudents = students;
      this.publishStudentReports = reportMap;
      this.selectedPublishStudents = students;
    } catch (err) {
      this.activeReport = null;
      this.selectedPublishStudents = [];
      this.toaster.error('Failed to load reports. Please try again.');
    }
  }

  // Move the selected reports back to Ready to Review. When syncStale is true,
  // the selected reports on older template versions are first synced to the
  // newest template, then the whole selection is moved back. When false, only
  // the non-stale selected reports are moved back and the stale ones are left
  // as Ready to Publish.
  async _performMoveBack({ syncStale }) {
    let selected = this.selectedPublishStudents.slice();
    if (!selected.length) {
      this.showTemplateSyncDialog = false;
      return;
    }
    // A closed period never syncs, whatever the caller asked for. The dialog
    // doesn't offer it there, so this only catches a stale dialog left open
    // across a sidepeek change — see isHistoricPublishPeriod for why.
    let sync = syncStale && !this.isHistoricPublishPeriod;
    let staleSet = new Set(this.selectedStaleStudents);
    let toMoveBack = sync
      ? selected
      : selected.filter((student) => !staleSet.has(student));
    if (!toMoveBack.length) {
      this.showTemplateSyncDialog = false;
      return;
    }

    this.isChangingStatus = true;
    try {
      if (sync) {
        await Promise.all(
          selected
            .filter((student) => staleSet.has(student))
            .map((student) => {
              let report = this.publishStudentReports[student.get('id')];
              return report
                ? this.studentReportActions.syncTemplate(report)
                : null;
            }),
        );
      }

      await Promise.all(
        toMoveBack.map((student) => {
          let report = this.publishStudentReports[student.get('id')];
          return report ? this.studentReportActions.submit(report) : null;
        }),
      );

      this.publishStudents = this.publishStudents.filter(
        (student) => !toMoveBack.includes(student),
      );
      this.selectedPublishStudents = this.selectedPublishStudents.filter(
        (student) => !toMoveBack.includes(student),
      );

      this.loadGroupReports();
      if (this.isHistoricPublishPeriod) {
        this.loadGroupHistoric();
      }
      if (!this.publishStudents.length) {
        this.closePublishSidepeek();
      }
    } catch (e) {
      this.toaster.error('Failed to change report status');
    } finally {
      this.isChangingStatus = false;
      this.showTemplateSyncDialog = false;
    }
  }

  @action
  selectGroupType(type) {
    this.parentController.set('selectedGroupType', type);
  }

  @action
  selectStudent(person) {
    this.router.transitionTo(
      'reporting.group.student',
      this.selectedGroup.get('id'),
      person.get('id'),
    );
  }

  @action
  toggleGroupStudents(groupId) {
    let expandedGroups = this.parentController.get('expandedGroups');
    let pos = expandedGroups.indexOf(groupId);
    if (pos > -1) {
      expandedGroups.splice(pos, 1);
    } else {
      expandedGroups.push(groupId);
      this.reportingGroupBrowser.ensureGroupPersonsLoaded(groupId);
    }
  }

  @action
  selectGroup(group) {
    this.router.transitionTo('reporting.group', group.get('id'));
  }

  @action
  selectReport(report, period) {
    // Closed periods no longer accept new drafts. The cards don't offer it,
    // so this only guards a caller that reaches the action another way —
    // see isHistoricPublishPeriod for why the restriction exists.
    if (period?.isHistoric) return;
    // Nor does a deleted template, whatever the period. The cards don't
    // offer it either; this is the same last-line guard.
    if (report?.deleteTime) return;
    this.selectedReport = report;
    // The period comes from the card that was clicked, not from a clock read
    // at save time. The card is showing a specific period — that is the one
    // the user is choosing, and it is the same index the backend counted
    // against, so the drafts land where the card says they will.
    this.selectedPeriodIndex = qpPeriod(period);
    this.selectedStudents = [];
    this.sidepeekOpen = true;
  }

  @action
  closeSidepeek() {
    // Block user-initiated close attempts (X, backdrop, ESC) while drafts
    // are mid-flight so they can't abandon createDraft halfway through.
    if (this.isCreatingDrafts) return;
    this._resetSidepeek();
  }

  @action
  toggleStudent(student) {
    if (this.selectedStudents.includes(student)) {
      this.selectedStudents = this.selectedStudents.filter(
        (s) => s !== student,
      );
    } else {
      this.selectedStudents = [...this.selectedStudents, student];
    }
  }

  @action
  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedStudents = [];
    } else {
      this.selectedStudents = this.selectableStudents;
    }
  }

  @action
  async createDrafts() {
    let selected = this.selectedReport;
    // Silently resolve to the current version if the picked template has
    // been superseded since the page loaded. Drafts (and the redirect
    // below) follow the resolved template.
    let report =
      await this.studentReportActions.resolveCurrentTemplate(selected);
    let reportId = report.get('id');
    let students = this.selectedStudents.slice();
    let year = this.dates.currentAcademicYear;
    // Stamp the period the selected card was showing. Recomputing it here
    // would be a second, independent answer to a question the card already
    // answered — and the card's index is the one the backend counted
    // against, so anything else files the drafts under a period the user
    // was never shown.
    //
    // Only a template version change forces a recompute: periodIndex is a
    // position in the version's own period list, so an index resolved
    // against the superseded version can mean a different period (or none)
    // on the one the drafts are actually created against.
    let periodIndex = this.selectedPeriodIndex;
    if (reportId !== selected.get('id') || typeof periodIndex !== 'number') {
      // Ask the counts response for the resolved template's current period
      // rather than working it out here. Every active template has an entry,
      // so this is populated whenever the card list rendered.
      let entry = (this.templateDraftInfo || {})[reportId];
      periodIndex =
        entry && typeof entry.periodIndex === 'number' ? entry.periodIndex : 0;
    }

    this.isCreatingDrafts = true;
    let unblockUnload = this.studentReportActions.blockUnload();

    try {
      await Promise.all(
        students.map((student) =>
          this.studentReportActions.createDraft({
            student,
            template: report,
            periodIndex,
            year,
          }),
        ),
      );

      this._resetSidepeek();
      // Name the period explicitly. Write's period params are sticky per
      // template, so omitting them reopens whatever period this template's
      // write screen last showed. After a historic card that is a past
      // period, and the drafts just created read as "No reports in progress".
      this.router.transitionTo('reporting.group.write', report.get('id'), {
        queryParams: {
          mode: null,
          periodIndex,
          periodEndDate: null,
          periodYear: year,
        },
      });
      this._refreshTemplateCounts(reportId);
    } catch (err) {
      this.toaster.error('Failed to create report drafts');
      // Active only — drafts can only be created in the current period, so
      // no past period can have moved.
      this.loadGroupReports();
    } finally {
      unblockUnload();
      this.isCreatingDrafts = false;
    }
  }

  @action
  writeOrStartReports(report, period) {
    let drafts = this.templateDraftInfo[report.get('id')];
    if (drafts && drafts.inProgress) {
      // Forward the caller's period rather than letting the write route fall
      // back to the template's current one — they differ whenever the entry
      // describes an earlier period.
      this.openWriteReports(report, period);
    } else {
      this.selectReport(report, period);
    }
  }

  @action
  async openWriteReports(report, period) {
    this.loadingReportId = report.get('id');
    this.activeReport = report;
    // Coming back from this screen should re-query historic counts only if a
    // historic card sent us there — see refreshHistoricIfNeeded.
    if (period?.isHistoric) {
      this.markHistoricStale();
    }
    try {
      await this.router.transitionTo(
        'reporting.group.write',
        report.get('id'),
        {
          queryParams: {
            mode: null,
            periodIndex: qpPeriod(period),
            periodEndDate: qpPeriodEnd(period),
            periodYear: qpPeriodYear(period),
          },
        },
      );
    } catch {
      // Aborted or redirected. Nothing to report, just stop the spinner.
    } finally {
      // The spinner covers the transition only. Left set, it outlives the
      // trip: back on the hub every card for this template (active and
      // historic share the id) spins and every Write button stays disabled.
      this.loadingReportId = null;
    }
  }

  @action
  openReviewReports(report, period) {
    this.activeReport = report;
    // Coming back from this screen should re-query historic counts only if a
    // historic card sent us there — see refreshHistoricIfNeeded.
    if (period?.isHistoric) {
      this.markHistoricStale();
    }
    this.router.transitionTo('reporting.group.review', report.get('id'), {
      queryParams: {
        periodIndex: qpPeriod(period),
        periodEndDate: qpPeriodEnd(period),
        periodYear: qpPeriodYear(period),
      },
    });
  }

  @action
  openViewReports(report, period) {
    this.activeReport = report;
    // Coming back from this screen should re-query historic counts only if a
    // historic card sent us there — see refreshHistoricIfNeeded.
    if (period?.isHistoric) {
      this.markHistoricStale();
    }
    this.router.transitionTo('reporting.group.view', report.get('id'), {
      queryParams: {
        periodIndex: qpPeriod(period),
        periodEndDate: qpPeriodEnd(period),
        periodYear: qpPeriodYear(period),
      },
    });
  }

  @action
  openWriteComments(report, period) {
    this.activeReport = report;
    // Coming back from this screen should re-query historic counts only if a
    // historic card sent us there — see refreshHistoricIfNeeded.
    if (period?.isHistoric) {
      this.markHistoricStale();
    }
    this.router.transitionTo('reporting.group.write', report.get('id'), {
      queryParams: {
        mode: 'leadership',
        periodIndex: qpPeriod(period),
        periodEndDate: qpPeriodEnd(period),
        periodYear: qpPeriodYear(period),
      },
    });
  }

  @action
  async openPublishReports(report, period) {
    if (this.isOpeningPublishReports) return;
    this.isOpeningPublishReports = true;
    this.activeReport = report;
    this.selectedPublishStudents = [];
    // Held for the retry path, which reloads the list after a partial run and
    // must ask for the same period rather than the current one.
    this.publishPeriod = period || null;

    try {
      await this._loadPublishStudents(report, period);
    } finally {
      this.isOpeningPublishReports = false;
    }
  }

  @action
  closePublishSidepeek() {
    // Block closing mid-publish — interrupting could leave reports published
    // without their freshly-captured visuals.
    if (this.isPublishing) return;
    this.publishSidepeekOpen = false;
    this.activeReport = null;
    this.publishStudents = null;
    this.publishStudentReports = null;
    this.selectedPublishStudents = [];
    this.publishPeriod = null;
    this.loadingReportId = null;
    this.showTemplateSyncDialog = false;
  }

  @action
  togglePublishStudent(student) {
    if (this.selectedPublishStudents.includes(student)) {
      this.selectedPublishStudents = this.selectedPublishStudents.filter(
        (s) => s !== student,
      );
    } else {
      this.selectedPublishStudents = [...this.selectedPublishStudents, student];
    }
  }

  @action
  togglePublishSelectAll() {
    if (!this.publishStudents) return;
    if (this.selectedPublishStudents.length === this.publishStudents.length) {
      this.selectedPublishStudents = [];
    } else {
      this.selectedPublishStudents = this.publishStudents;
    }
  }

  @action
  registerPublishRun(fn) {
    this._runPublishCapture = fn;
  }

  // Per-item publish, invoked by PublishReportsRunner after it has captured
  // and saved that report's visuals.
  @action
  publishOneReport(studentReport) {
    return this.studentReportActions.publish(studentReport);
  }

  @action
  async publishReports() {
    // Publishing now goes through PublishReportsRunner, which renders each
    // selected report off-screen, recaptures its strand/progress visuals, and
    // publishes it — sequentially. The runner drives the progress modal and
    // blocks navigation; we just gate the sidepeek (closePublishSidepeek and
    // the route's willTransition both check isPublishing) and flip the save
    // button while it runs.
    if (!this._runPublishCapture) return;
    // Reset the nav-block toast flag so each run starts clean — the
    // partial-failure path leaves the sidepeek open without a transition, so
    // the flag would otherwise carry a stale `true` into the next run.
    this.publishNavBlocked = false;
    this.isPublishing = true;
    let summary;
    try {
      summary = await this._runPublishCapture();
    } finally {
      // Reset BEFORE the close/refresh below — closePublishSidepeek and the
      // route guard both no-op while isPublishing is true.
      this.isPublishing = false;
    }
    // null = the run aborted or errored; the runner has already toasted.
    if (!summary) return;

    let { total, published, failures, failed } = summary;
    // Refresh the group state-count cards behind the sidepeek. Historic only
    // when this run belonged to a past period — publishing from an active card
    // cannot have changed one.
    this.loadGroupReports();
    if (this.isHistoricPublishPeriod) {
      this.loadGroupHistoric();
    }

    if (failures) {
      // Single summary toast naming the reports that didn't publish (capped so
      // a large batch doesn't produce an unwieldy message). They stay ready to
      // publish for a retry.
      let names = failed || [];
      let shown = names.slice(0, 5).join(', ');
      let extra = names.length > 5 ? ` and ${names.length - 5} more` : '';
      this.toaster.error(
        `Published ${published} of ${total}. ${failures} left as ready to publish: ${shown}${extra}.`,
      );
      // Keep the sidepeek open and refresh its list so the published students
      // drop off and the remaining (failed) reports can be retried. Guard on
      // publishSidepeekOpen too: if the user closed it during the await, we
      // must not let _loadPublishStudents re-open it.
      if (this.activeReport && this.publishSidepeekOpen) {
        await this._loadPublishStudents(this.activeReport, this.publishPeriod);
      }
    } else {
      this.toaster.success(`${published} report(s) published.`);
      this.closePublishSidepeek();
    }
  }

  // "Move back to Ready to Review" entry point. Always available when a
  // selection exists. If any selected report is on an older template version,
  // confirm via the template-sync dialog first; otherwise move them straight
  // back.
  @action
  moveBackToReview() {
    if (!this.selectedPublishStudents.length) return;
    if (this.selectedHaveStaleTemplate) {
      this.showTemplateSyncDialog = true;
    } else {
      this._performMoveBack({ syncStale: false });
    }
  }

  // Dialog "Update & move all back": sync the stale selected reports to the
  // newest template, then move the whole selection back to Ready to Review.
  @action
  confirmSyncAndMoveBack() {
    this._performMoveBack({ syncStale: true });
  }

  // Dialog "Move back the others only": move back just the non-stale selected
  // reports; leave the stale ones as Ready to Publish.
  @action
  moveBackWithoutStale() {
    this._performMoveBack({ syncStale: false });
  }

  @action
  cancelTemplateSync() {
    if (this.isChangingStatus) return;
    this.showTemplateSyncDialog = false;
  }
}
